import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { axiosInstance } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faChartLine } from '@fortawesome/free-solid-svg-icons';
import Navbar from '../../components/Navbar';
import ArtozphereCartBar from '../../components/artozphere/ArtozphereCartBar';
import { useCart } from '../../context/CartContext';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useSavedArtworks } from '../../context/SavedArtworksContext';
import { galleryFollowApi } from '../../api/galleryFollowApi';
import { getArtworkImageSrc } from '../../utils/artworkImage';

const isMongoObjectId = (value) => /^[a-f\d]{24}$/i.test(value);

const ArtozphereArtworkDetail = () => {
  const { artworkId } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [relatedArtworks, setRelatedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToCart, isInCart } = useCart();
  const { user } = useAuthContext();
  const { isArtworkSaved, toggleSaveArtwork } = useSavedArtworks();
  const [isFollowingGallery, setIsFollowingGallery] = useState(false);
  const [galleryFollowers, setGalleryFollowers] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  const isPurchasable =
    Number(artwork?.price) > 0 && ['available', 'approved'].includes(artwork?.status);

  useEffect(() => {
    setArtwork(null);
    setRelatedArtworks([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [artworkId]);

  useEffect(() => {
    const fetchArtwork = async () => {
      if (!artworkId) {
        setError('No artwork ID provided');
        return;
      }

      try {
        setLoading(true);
        const artworkUrl = isMongoObjectId(artworkId)
          ? `/artworks/${artworkId}`
          : `/artworks/slug/${artworkId}`;
        const response = await axiosInstance.get(artworkUrl);
        setArtwork(response.data);

        const relatedResponse = await axiosInstance.get('/artworks/marketplace');
        const relatedData = Array.isArray(relatedResponse.data)
          ? relatedResponse.data
          : relatedResponse.data?.artworks || [];
        setRelatedArtworks(
          relatedData
            .filter((item) => item._id !== response.data._id)
            .slice(0, 3)
        );
        setError(null);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load artwork details');
      } finally {
        setLoading(false);
      }
    };

    fetchArtwork();
    axiosInstance.put(`/artworks/${artworkId}/view`).catch(console.error);
  }, [artworkId]);

  useEffect(() => {
    const galleryRef = artwork?.gallery?.galleryId || artwork?.gallery?._id;
    if (!galleryRef || !user) {
      setIsFollowingGallery(false);
      return;
    }

    let cancelled = false;

    const loadFollowStatus = async () => {
      try {
        const response = await galleryFollowApi.checkFollowing(galleryRef);
        if (!cancelled) {
          setIsFollowingGallery(Boolean(response?.isFollowing));
        }
      } catch (err) {
        console.error('Failed to check gallery follow status:', err);
      }
    };

    loadFollowStatus();
    return () => {
      cancelled = true;
    };
  }, [artwork?.gallery, user]);

  useEffect(() => {
    if (artwork?.gallery?.followers != null) {
      setGalleryFollowers(artwork.gallery.followers);
    }
  }, [artwork?.gallery?.followers]);

  const handleToggleGalleryFollow = async () => {
    const galleryRef = artwork?.gallery?.galleryId || artwork?.gallery?._id;
    if (!galleryRef || followLoading) return;

    setFollowLoading(true);
    try {
      const response = await galleryFollowApi.toggleFollow(galleryRef);
      const following = Boolean(response?.isFollowing);
      setIsFollowingGallery(following);
      setGalleryFollowers((prev) => Math.max(0, prev + (following ? 1 : -1)));
    } catch (err) {
      console.error('Failed to toggle gallery follow:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleSave = () => {
    if (!user || !artwork) return;
    toggleSaveArtwork({
      _id: artwork._id,
      id: artwork._id,
      title: artwork.title,
      artist: artwork.artist,
      image: getArtworkImageSrc(artwork),
      views: artwork.views,
      price: artwork.price,
      slug: artwork.slug,
    });
  };

  const imageSrc = getArtworkImageSrc(artwork);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-[60vh] ml-0 md:ml-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-[60vh] ml-0 md:ml-20">
          <div className="text-red-500 text-center">
            <h2 className="text-2xl font-bold mb-2">Error</h2>
            <p>{error}</p>
          </div>
        </div>
      </>
    );
  }

  if (!artwork) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-[60vh] ml-0 md:ml-20">
          <div className="text-gray-500 text-center">
            <h2 className="text-2xl font-bold mb-2">Not Found</h2>
            <p>Artwork not found</p>
          </div>
        </div>
      </>
    );
  }

  const saved = isArtworkSaved(artwork._id);
  const emotiologyPath = `/artwork/${artwork._id}`;

  return (
    <>
      <Navbar />
      <div className="mx-auto px-4 pt-8 pb-16 ml-0 md:ml-20 md:px-8">
        <div className="flex justify-end mb-4">
          <ArtozphereCartBar />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl sm:text-5xl font-serif mb-2">{artwork.title}</h1>
              {artwork.gallery?.displayName && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Gallery
                    </p>
                    <p className="text-lg font-medium text-gray-900">
                      {artwork.gallery.displayName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {galleryFollowers} follower{galleryFollowers === 1 ? '' : 's'}
                    </p>
                  </div>
                  {user && (
                    <button
                      type="button"
                      onClick={handleToggleGalleryFollow}
                      disabled={followLoading}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        isFollowingGallery
                          ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      {followLoading ? '...' : isFollowingGallery ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <h3 className="uppercase font-semibold border-b pb-1 mb-2 text-sm text-gray-500">
                Dimensions
              </h3>
              <p>
                {artwork.dimensions?.height != null && artwork.dimensions?.width != null
                  ? `${artwork.dimensions.height} cm (H) × ${artwork.dimensions.width} cm (W)`
                  : 'Dimensions not specified'}
              </p>
            </div>

            <div>
              <h3 className="uppercase font-semibold border-b pb-1 mb-2 text-sm text-gray-500">
                Materials
              </h3>
              <p>{artwork.medium || 'Not specified'}</p>
            </div>

            <div>
              <h3 className="uppercase font-semibold border-b pb-1 mb-2 text-sm text-gray-500">
                Location
              </h3>
              <p>{artwork.location || 'Australia'}</p>
            </div>

            <div>
              <h3 className="italic font-semibold text-gray-700 mb-1">Provenance</h3>
              <p className="text-sm text-gray-600">
                {artwork.provenance ||
                  'Part of the "Rising Sun" series by Lee Kang-In, this piece is now in the private collection of a Sydney business owner and is currently on exhibition in Broadway...'}
              </p>
            </div>

            <div>
              <h3 className="italic font-semibold text-gray-700 mb-1">Author's Notes</h3>
              <p className="text-sm text-gray-600 whitespace-pre-line">
                {artwork.authorNotes ||
                  "In a city where ancient temples brush shoulders with circuit boards, it explores the fragile coexistence of human softness and digital precision."}
              </p>
            </div>
          </div>

          <div className="flex flex-col space-y-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-3xl font-semibold">${artwork.price}</div>
                <button
                  type="button"
                  onClick={() => addToCart(artwork)}
                  disabled={!isPurchasable || isInCart(artwork._id)}
                  className={`bg-green-600 text-white px-6 py-2 rounded transition-colors ${
                    !isPurchasable || isInCart(artwork._id)
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-green-700'
                  }`}
                >
                  {!isPurchasable
                    ? 'Unavailable'
                    : isInCart(artwork._id)
                      ? 'In Cart'
                      : 'Add to Cart'}
                </button>
                <Link
                  to={emotiologyPath}
                  className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 transition-colors text-sm font-medium"
                >
                  Emotiology
                </Link>
              </div>

              <div className="flex space-x-4 text-sm text-gray-600 items-center">
                <div>{artwork.views || 0} Views</div>
                <button
                  type="button"
                  onClick={handleSave}
                  className={`flex items-center gap-1 transition-colors ${
                    saved ? 'text-red-500' : 'hover:text-red-500'
                  }`}
                >
                  <FontAwesomeIcon icon={faHeart} />
                  <span>{saved ? 'Saved' : 'Save'}</span>
                </button>
                <Link
                  to={`/artozphere/artwork/${artwork.slug || artwork._id}/stocks`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-emerald-400 text-sm font-medium hover:bg-slate-800 border border-slate-700 transition"
                >
                  <FontAwesomeIcon icon={faChartLine} className="w-4 h-4" />
                  Stocks
                </Link>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={artwork.title}
                  className="w-auto max-w-full h-auto object-cover rounded-lg shadow-md"
                />
              ) : (
                <div className="flex h-64 w-full max-w-md items-center justify-center rounded-lg bg-gray-100 text-sm text-gray-500">
                  No image available
                </div>
              )}
            </div>
          </div>
        </div>

        {relatedArtworks.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-serif mb-6">Similar Artworks</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArtworks.map((related) => (
                <Link
                  key={related._id}
                  to={`/artozphere/artwork/${related.slug || related._id}`}
                  className="group"
                >
                  <div className="mb-3">
                    {getArtworkImageSrc(related) ? (
                      <img
                        src={getArtworkImageSrc(related)}
                        alt={related.title}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="flex h-48 w-full items-center justify-center rounded-lg bg-gray-100 text-sm text-gray-400">
                        No image
                      </div>
                    )}
                  </div>
                  <h3 className="font-medium group-hover:text-blue-600">{related.title}</h3>
                  <p className="text-gray-500">${related.price}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ArtozphereArtworkDetail;
